import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LeafIcon, RotateCcwIcon, TruckIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { getProduct } from '../../utils/products';
import type { Product } from '../../types/product';
import { Stars } from '../shop/Stars';
import { QuantityStepper } from '../shop/QuantityStepper';
const badgeLabel: Record<string, string> = {
  bestseller: 'Bestseller',
  nieuw: 'Nieuw',
  cadeau: 'Cadeau-tip'
};
export function BuyBox({
  product
}: {
  product: Product;
}) {
  const {
    add
  } = useCart();
  const navigate = useNavigate();
  const [variantId, setVariantId] = useState(product.variants[0].id);
  const [qty, setQty] = useState(1);
  const variant = product.variants.find(v => v.id === variantId) ?? product.variants[0];
  const included = (product.includes ?? []).map(s => getProduct(s)).filter((p): p is Product => Boolean(p));
  const isSizeVariant = /\d/.test(variant.label);
  const buyNow = () => {
    add(product.slug, variant.id, qty, false);
    navigate('/afrekenen');
  };
  return <div data-id="element-258" data-mp="1du5sorcmngee">
      {product.badges.length > 0 && <div className="flex gap-2" data-id="element-259" data-mp="18u9rt0z88532">
          {product.badges.map(b => <span key={b} className="rounded-full bg-surface px-2.5 py-1 text-xs font-medium" data-id="element-260" data-mp="18wdzlu08xo8m">{badgeLabel[b]}</span>)}
        </div>}
      <h1 className="mt-4 text-4xl font-semibold tracking-display text-ink sm:text-5xl" data-id="element-261" data-mp="zuwn0lm1s4oo">{product.name}</h1>
      <p className="mt-1.5 text-lg text-muted" data-id="element-262" data-mp="v2d191d7i38s">{product.subtitle}</p>
      <div className="mt-4" data-id="element-263" data-mp="q3iks62poqru">
        <a href="#details" className="inline-block" data-id="element-264" data-mp="q4tns3oc1uhw">
          <Stars rating={product.rating} reviews={product.reviews} size="md" data-id="element-265" data-mp="l58n9otile32" />
        </a>
      </div>

      <p className="mt-6 flex items-baseline gap-3" data-id="element-266" data-mp="h33ustesyni0">
        <span className="text-3xl font-semibold tracking-tight tabular-nums" data-id="element-267" data-mp="h2u1avujty7k">{formatPrice(variant.price * qty)}</span>
        {variant.compareAt && <span className="text-lg text-muted line-through" data-id="element-268" data-mp="2gwrjo77qojj">{formatPrice(variant.compareAt * qty)}</span>}
        {qty > 1 && <span className="text-sm text-muted" data-id="element-269" data-mp="6irt8tt233xu">{qty} × {formatPrice(variant.price)}</span>}
      </p>
      <p className="mt-4 text-lg leading-relaxed text-ink" data-id="element-270" data-mp="3ooeh1xghy8dc">{product.benefit}</p>

      {product.variants.length > 1 && <fieldset className="mt-8" data-id="element-271" data-mp="3tnr52lj3tlhi">
          <legend className="text-sm font-medium" data-id="element-272" data-mp="3tp2bkt3xp7cb">
            {isSizeVariant ? 'Inhoud' : 'Variant'}: <span className="text-muted" data-id="element-273" data-mp="l994f4vht87x">{variant.label}</span>
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-274" data-mp="q5prrwuvbijy">
            {product.variants.map(v => {
          const active = v.id === variant.id;
          return <button key={v.id} type="button" role="radio" aria-checked={active} onClick={() => setVariantId(v.id)} className={`flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors duration-150 ${active ? 'border-ink bg-white ring-1 ring-ink' : 'border-line bg-white hover:border-muted'}`} data-id="element-275" data-mp="gf4uqduuem35">
                  <span className="text-sm font-semibold" data-id="element-276" data-mp="32eeiz51p3qpe">{v.label}</span>
                  <span className="text-sm text-muted" data-id="element-277" data-mp="2ydcg8e8kkwio">{formatPrice(v.price)}</span>
                </button>;
        })}
          </div>
        </fieldset>}

      {included.length > 0 && <div className="mt-8" data-id="element-278" data-mp="25pys95munbap">
          <p className="text-sm font-medium" data-id="element-279" data-mp="25qrrlljhayit">In deze set</p>
          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-280" data-mp="2jphp32a7n07p">
            {included.map(p => <li key={p.slug} data-id="element-281" data-mp="2eq546n2wg37r">
                <Link to={`/product/${p.slug}`} className="flex items-center gap-2 rounded-full border border-line bg-white py-1 pl-1 pr-3 text-sm hover:border-ink" data-id="element-282" data-mp="2ep3q09ql70lt">
                  <img src={p.image} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-283" data-mp="3q5jwveo2s8x2" />
                  {p.name}
                </Link>
              </li>)}
          </ul>
        </div>}

      <div className="mt-8 flex gap-3" data-id="element-284" data-mp="u8e1pfs18bou">
        <QuantityStepper value={qty} onChange={setQty} size="lg" data-id="element-285" data-mp="u95w4n9w83dj" />
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className="h-14 flex-1 whitespace-nowrap rounded-full bg-ink px-6 text-base font-medium text-canvas transition-colors duration-150 hover:bg-clay" data-id="element-286" data-mp="z7qtmexrg8ya">
          In winkelwagen
        </button>
      </div>
      <button type="button" onClick={buyNow} className="mt-3 h-14 w-full whitespace-nowrap rounded-full border border-ink/15 bg-white text-base font-medium text-ink transition-colors duration-150 hover:border-ink" data-id="element-287" data-mp="1d672ocj3ibjl">
        Direct afrekenen
      </button>

      <p className="mt-5 flex items-center gap-2 text-sm" data-id="element-288" data-mp="1rxphw844s21w">
        <span className="h-2 w-2 rounded-full bg-olive" aria-hidden="true" data-id="element-289" data-mp="1vy9dv8cywzmg" />
        <span data-id="element-290" data-mp="1vw6pesrgreju">
          <strong className="font-medium text-olive" data-id="element-291" data-mp="3ggbtw0s56yq5">Op voorraad</strong> — vóór 22:00 besteld, morgen in huis
        </span>
      </p>

      <ul className="mt-8 grid gap-3 border-t border-line pt-6 text-sm text-muted" data-id="element-292" data-mp="3cegp2mb0637m">
        <li className="flex items-center gap-3" data-id="element-293" data-mp="37f3vlipmlxw2"><TruckIcon className="h-4 w-4 text-ink" data-id="element-294" data-mp="37g5gsg0cw0ri" /> Gratis verzending vanaf € 50</li>
        <li className="flex items-center gap-3" data-id="element-295" data-mp="32hu8watmg0bx"><RotateCcwIcon className="h-4 w-4 text-ink" data-id="element-296" data-mp="32ivkqhigoqup" /> 30 dagen bedenktijd</li>
        <li className="flex items-center gap-3" data-id="element-297" data-mp="3y3skwal0df6"><LeafIcon className="h-4 w-4 text-ink" data-id="element-298" data-mp="3v5gbbfr44yx7" /> 100% puur, zonder toevoegingen</li>
      </ul>

      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-line bg-canvas/95 px-5 py-3 backdrop-blur lg:hidden" data-id="element-299" data-mp="3qehrw7atvkpq">
        <div className="min-w-0 flex-1" data-id="element-300" data-mp="3lelpgchs41ca">
          <p className="truncate text-sm font-medium" data-id="element-301" data-mp="3ldk9btlwoonp">{product.name} · {variant.label}</p>
          <p className="text-sm font-semibold tabular-nums" data-id="element-302" data-mp="mrhde8kkz3h2">{formatPrice(variant.price * qty)}</p>
        </div>
        <button type="button" onClick={() => add(product.slug, variant.id, qty)} className="h-12 whitespace-nowrap rounded-full bg-ink px-6 text-sm font-medium text-canvas" data-id="element-303" data-mp="2c6ieku1fxz6t">
          In winkelwagen
        </button>
      </div>
    </div>;
}